import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Navigation, LogIn, LogOut, RefreshCw, CheckCircle2, XCircle, Crosshair,
} from "lucide-react";

export default function Attendance() {
  const [settings, setSettings] = useState(null);
  const [pos, setPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState("out");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, st, h] = await Promise.all([
      api.get("/settings"), api.get("/attendance/status"), api.get("/attendance/me"),
    ]);
    setSettings(s.data); setStatus(st.data.status); setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const locate = useCallback(() => {
    setLocating(true);
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }); setLocating(false); },
      (err) => { toast.error(`Location error: ${err.message}`); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  useEffect(() => { locate(); }, [locate]);

  const haversine = (a, b, c, d) => {
    const R = 6371000, r = Math.PI / 180;
    const dphi = (c - a) * r, dl = (d - b) * r;
    const x = Math.sin(dphi / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin(dl / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };

  const distance = settings && pos ? haversine(pos.lat, pos.lng, settings.lat, settings.lng) : null;
  const inRange = distance != null && settings && distance <= settings.radius;

  const act = async (action) => {
    if (!pos) return toast.error("Capture your location first");
    setBusy(true);
    try {
      const res = await api.post(`/attendance/${action}`, { lat: pos.lat, lng: pos.lng });
      toast.success(`${action === "checkin" ? "Checked in" : "Checked out"} at ${res.data.display_time}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const pct = distance != null && settings ? Math.min(100, (distance / (settings.radius * 1.5)) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="attendance-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Geofenced Attendance</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Check In / Out</h1>
        <p className="text-muted-foreground mt-1">You must be within the allowed radius of the hostel to record attendance.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 border-t-4 border-emerald-500 pt-4">
        {/* Radar + status */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-lg font-bold">Location status</h2>
            <Button size="sm" variant="secondary" onClick={locate} disabled={locating} data-testid="refresh-location-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${locating ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          <div className="relative mx-auto h-56 w-56 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-border" />
            <div className="absolute inset-8 rounded-full border border-border" />
            <div className={`absolute h-24 w-24 rounded-full flex items-center justify-center ${inRange ? "radar-ping" : ""}`}>
              <div className={`h-16 w-16 rounded-full flex items-center justify-center ${inRange ? "bg-primary" : "bg-muted"}`}>
                <MapPin className={`h-7 w-7 ${inRange ? "text-primary-foreground" : "text-muted-foreground"}`} />
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            {distance != null ? (
              <>
                <p className="text-4xl font-display font-extrabold" data-testid="distance-readout">{Math.round(distance)}<span className="text-lg text-muted-foreground"> m</span></p>
                <p className="text-sm text-muted-foreground">from {settings?.label}</p>
                <div className="mt-3 h-2 w-full rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${inRange ? "bg-primary" : "bg-destructive"}`} style={{ width: `${pct}%` }} />
                </div>
                <Badge className={`mt-4 border-0 ${inRange ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="range-badge">
                  {inRange ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> IN RANGE</> : <><XCircle className="h-3.5 w-3.5 mr-1" /> OUT OF RANGE</>}
                </Badge>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">{locating ? "Locating you…" : "Location unavailable"}</p>
            )}
          </div>

          {pos && (
            <p className="mt-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Crosshair className="h-3 w-3" /> {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)} (±{Math.round(pos.acc)}m)
            </p>
          )}
        </div>

        {/* Actions + history */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <Navigation className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Current status</p>
                <p className="font-display text-xl font-bold capitalize" data-testid="attendance-status">
                  {status === "in" ? "Checked In" : "Checked Out"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button className="h-14 text-base" disabled={!inRange || busy || status === "in"} onClick={() => act("checkin")} data-testid="attendance-checkin-btn">
                <LogIn className="h-5 w-5 mr-2" /> Check In
              </Button>
              <Button className="h-14 text-base" variant="secondary" disabled={!inRange || busy || status === "out"} onClick={() => act("checkout")} data-testid="attendance-checkout-btn">
                <LogOut className="h-5 w-5 mr-2" /> Check Out
              </Button>
            </div>
            {!inRange && distance != null && (
              <p className="mt-3 text-xs text-destructive text-center">Move within {settings?.radius}m to enable buttons.</p>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="font-display text-lg font-bold mb-4">My recent activity</h2>
            {history.length === 0 && <p className="text-sm text-muted-foreground">No records yet.</p>}
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`history-${h.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${h.action === "checkin" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
                      {h.action === "checkin" ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
                    </div>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold capitalize">{h.action === "checkin" ? "Check In" : "Check Out"}</p>
                      <p className="text-xs text-muted-foreground">{h.display_time}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{Math.round(h.distance)}m</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
